import React from "react";
import { DialogActions, DialogContent, Box } from "@material-ui/core";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import { ModalActions } from "../models/ModalActions";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";

const useStyles = makeStyles({
  icon: {
    position: "absolute",
    right: "1px",
    top: "1px",
    color: "black",
  },
  title: {
    marginRight: "16px",
  },
  actions: {
    border: "none",
  },
  body: {
    borderBottom: "none",
  },
  btn: { margin: "3px" },
  customDialogBoxHeight: {
    minHeight: "80vh",
    maxHeight: "80vh",
  },
});

type Props = {
  isOpen: boolean;
  title?: string;
  children: React.ReactNode;
  PaperProps?: object;
  fullScreen?: boolean;
  size?: string;
  height?: string;
  actions?: ModalActions[];
  closeModal?: () => void;
  positionButton?: "flex-start" | "center" | "flex-end";
};

export const Modal: React.FunctionComponent<Props> = ({
  isOpen,
  title,
  children,
  actions = [],
  closeModal = () => {},
  PaperProps = {},
  fullScreen = false,
  positionButton = "flex-end",
  size = "",
  height,
}) => {
  const classNames = useStyles();
  const buttonActions = actions.map((action: ModalActions) => (
    <Button
      color="primary"
      className={classNames.btn}
      variant={action.variant || "contained"}
      key={action.label}
      onClick={action.handler}
      disabled={action.disabled}
    >
      {action.label}
    </Button>
  ));

  const dialogTitle = (
    <DialogTitle>
      <Typography className={classNames.title}>{title}</Typography>
      <IconButton onClick={closeModal} className={classNames.icon}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
  );

  const dialogContent = (
    <>
      {title ? dialogTitle : null}
      <DialogContent dividers className={classNames.body}>
        {children}
      </DialogContent>
      <DialogActions className={classNames.actions}>
        <Box
          flexGrow={1}
          display="flex"
          alignItems="center"
          justifyContent={positionButton}
        >
          {buttonActions}
        </Box>
      </DialogActions>
    </>
  );

  // Manually create a conditional dialog max width due to issue in accessing dynamic size from props.
  // Error appears here: maxWidth={size}
  let dialogContentView = (
    <Dialog open={isOpen} PaperProps={PaperProps} fullScreen={fullScreen}>
      {dialogContent}
    </Dialog>
  );
  if (size === "xs")
    dialogContentView = (
      <Dialog open={isOpen} maxWidth="xs" PaperProps={PaperProps}>
        {dialogContent}
      </Dialog>
    );
  if (size === "sm")
    dialogContentView = (
      <Dialog open={isOpen} maxWidth="sm" PaperProps={PaperProps}>
        {dialogContent}
      </Dialog>
    );
  if (size === "md")
    dialogContentView =
      height === "lg" ? (
        <Dialog
          open={isOpen}
          classes={{ paper: classNames.customDialogBoxHeight }}
          maxWidth="md"
          PaperProps={PaperProps}
        >
          {dialogContent}
        </Dialog>
      ) : (
        <Dialog open={isOpen} maxWidth="md" PaperProps={PaperProps}>
          {dialogContent}
        </Dialog>
      );
  if (size === "lg")
    dialogContentView = (
      <Dialog open={isOpen} maxWidth="lg" PaperProps={PaperProps}>
        {dialogContent}
      </Dialog>
    );

  return isOpen ? dialogContentView : null;
};
